import React from "react";
import { Select as AntSelect } from "antd";
import type { SelectProps as AntSelectProps } from "antd";
import styled from "styled-components";
import { ArrowDown01Icon, Tick02Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { radii, shadow } from "src/theme/ui";
import { FieldSize, fieldEdge, fieldHeight } from "./field";

/* The dropdown's own styling lives in theme/GlobalStyle, not here: Ant Design
   portals it to the end of body, and every select in the app should be able to
   opt into the same panel by name — including the ones whose trigger is not
   this component, like the borderless grouping control in a card heading. */

/**
 * Select — football's dropdown for option lists that are known up front
 * (statuses, roles, groupings), ported.
 *
 * The original is Radix Select. Ant Design's is kept underneath here because
 * it carries the same behaviour worth having — Enter and Space open it, arrows
 * rove, typing jumps to a match, Escape closes and returns focus, and the menu
 * portals above the layout — and reimplementing that on top of a div is how
 * you end up with a select that a keyboard cannot drive. What is ported is
 * everything you can see.
 *
 * Visually it is the field: the trigger wears the same 10px corner and edge as
 * an `Input`, and the menu is a 12px panel of 34px rows. The menu is one of
 * the few surfaces allowed a shadow, because it can be dismissed.
 */

/** The trigger. Both heights come from the field scale, not a t-shirt size. */
const Trigger = styled(AntSelect)<{ $size: FieldSize }>`
  width: 100%;

  /* Ant Design gives the selector its height through an ::after carrying a
     non-breaking space, so the height has to be set in both places or the
     value rides high in its box. */
  &&& .ant-select-selector {
    height: ${({ $size }) => fieldHeight[$size]}px;
    padding: 0 12px;
    border: 1px solid ${({ $size }) => fieldEdge[$size]};
    border-radius: ${radii.md}px;
    background: ${colors.white};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &&& .ant-select-selector::after,
  &&& .ant-select-selection-item,
  &&& .ant-select-selection-placeholder {
    line-height: ${({ $size }) => fieldHeight[$size] - 2}px;
  }

  &&& .ant-select-selection-item {
    padding-right: 18px;
    font-size: 13px;
    color: ${colors.gray[900]};
  }

  &&& .ant-select-selection-placeholder {
    font-size: 13px;
    color: ${colors.gray[500]};
  }

  &&&:hover .ant-select-selector {
    border-color: ${colors.gray[400]};
  }

  /* Open and focused both take the accent line under a soft accent ring. */
  &&&.ant-select-focused .ant-select-selector,
  &&&.ant-select-open .ant-select-selector {
    border-color: ${colors.primary[200]};
    box-shadow: ${shadow.focusRing};
  }

  &&& .ant-select-arrow {
    right: 11px;
    width: 13px;
    height: 13px;
    margin-top: -6.5px;
    color: ${colors.gray[500]};
  }

  &&&.ant-select-disabled .ant-select-selector {
    background: ${colors.gray[50]};
    color: ${colors.gray[400]};
    cursor: not-allowed;
  }
`;



export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<AntSelectProps<string>, "size" | "options"> {
  options?: SelectOption[];
  /** `control` (default) is the 34px page control; `field` the 38px form one. */
  size?: FieldSize;
}

const Select: React.FC<SelectProps> = ({
  size = "control",
  dropdownClassName,
  ...props
}) => (
  <Trigger
      $size={size}
      dropdownClassName={dropdownClassName}
      suffixIcon={<HugeIcon icon={ArrowDown01Icon} size={13} strokeWidth={2} />}
      menuItemSelectedIcon={<HugeIcon icon={Tick02Icon} size={13} />}
      {...props}
  />
);

export default Select;
